import './ImageUpload.scss';
import React, {
    FC,
    useState,
    ChangeEvent,
    DragEvent,
    CSSProperties,
} from 'react';
import classNames from 'classnames';
import { getHumanReadableFileSize, useObjectUrls } from '@/utils';
import type { Photo as ExistingPhotoFields } from '@/generated/graphql';
import Photo from '@/components/media/Photo/Photo';

type SingleUploadCallback = (file: File | null) => void;
type MultipleUploadCallback = (files: File[] | null) => void;

type ImageUploadProps = {
    buttonLabel?: string;
    labelWidth?: string | number | boolean;
    style?: CSSProperties;
    disabled?: boolean;
    multiple?: boolean;
    accept?: string[];
    existingPhotoFields?: ExistingPhotoFields & { id?: number };
    className?: string;
    onChangeCallback?: SingleUploadCallback | MultipleUploadCallback;
    required?: boolean;
    photoPurpose?: 'photo' | 'animation';
};

const ImageUpload: FC<ImageUploadProps> = ({
    buttonLabel = 'Datei wählen',
    labelWidth,
    style,
    disabled,
    required,
    multiple,
    accept = ['image/png', 'image/jpeg'],
    existingPhotoFields,
    className,
    onChangeCallback,
    photoPurpose = 'photo',
}) => {
    const [uploadedImages, setUploadedImages] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const getObjectUrl = useObjectUrls();
    const isAnimation = photoPurpose === 'animation';

    const processFiles = (files: File[]) => {
        if (!files.length) return;

        if (multiple) {
            (onChangeCallback as MultipleUploadCallback)?.(files);
        } else {
            (onChangeCallback as SingleUploadCallback)?.(files[0]);
        }

        setUploadedImages(
            files.map((file: File) => ({
                isValidFileType: accept.includes(file.type),
                type: file.type,
                size: getHumanReadableFileSize(file.size),
                src: getObjectUrl(file),
                name: file.name,
            })),
        );
    };

    const onFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        processFiles([...(e.target.files ?? [])]);
    };

    const onDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        processFiles([...(e.dataTransfer.files ?? [])]);
    };

    const onDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault(); // Required for drop to work
        if (!disabled) setIsDragging(true);
    };

    const onDragLeave = () => setIsDragging(false);

    const customStyle = {
        ...style,
        ...(labelWidth && labelWidth !== true
            ? {
                  '--label-width':
                      typeof labelWidth === 'number'
                          ? `${labelWidth}px`
                          : labelWidth,
              }
            : {}),
    } as CSSProperties;

    const fieldLabel = accept.includes('image/png')
        ? 'Bild (JPG, PNG)'
        : 'Bild (JPG)';

    return (
        <div
            className={classNames(
                'ImageUpload',
                {
                    isDragging: isDragging,
                    isRequired: required,
                },
                className,
            )}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            <div
                className={classNames('ImageUpload_inputContainer', {
                    hasLabelWidth: !!labelWidth,
                })}
            >
                {fieldLabel && (
                    <span
                        className={classNames('ImageUpload_label', {
                            isRequired: required,
                        })}
                        style={customStyle}
                    >
                        {fieldLabel}
                    </span>
                )}

                <label className="ImageUpload_trigger">
                    <span
                        className={classNames('ImageUpload_button', {
                            disabled: disabled,
                        })}
                    >
                        {buttonLabel}
                    </span>
                    {!disabled && (
                        <input
                            className="invisible"
                            type="file"
                            accept={accept.join(',')}
                            multiple={multiple}
                            onChange={onFileInputChange}
                        />
                    )}
                </label>
            </div>

            {/* ATM multiple is not used or properly supported */}
            {!multiple && (
                <section className="ImageUpload_output">
                    {!isAnimation && (
                        <section className="ImageUpload_outPutSlot">
                            <h2 className="ImageUpload_outPutHeading">
                                Aktuelles Bild
                            </h2>
                            <div
                                className={classNames(
                                    'ImageUpload_outPutImage',
                                    {
                                        isEmpty: !existingPhotoFields,
                                    },
                                )}
                            >
                                {existingPhotoFields && (
                                    <Photo
                                        fields={existingPhotoFields}
                                        imageSize="xs"
                                    />
                                )}
                            </div>
                        </section>
                    )}

                    <section className="ImageUpload_outPutSlot">
                        <h2 className="ImageUpload_outPutHeading">
                            {isAnimation ? 'Ankerframe' : 'Neues Bild'}
                        </h2>
                        <div
                            className={classNames('ImageUpload_outPutImage', {
                                isEmpty: !uploadedImages[0]?.isValidFileType,
                            })}
                        >
                            {uploadedImages.length > 0 && (
                                <>
                                    {' '}
                                    {uploadedImages[0].isValidFileType ? (
                                        <img
                                            className="ImageUpload_fileThumb"
                                            src={uploadedImages[0].src}
                                            alt={uploadedImages[0].name}
                                        />
                                    ) : (
                                        <div className="ImageUpload_error">
                                            Format {uploadedImages[0].type}{' '}
                                            nicht erlaubt!
                                        </div>
                                    )}
                                    <div className="ImageUpload_fileInfo">
                                        <span className="ImageUpload_fileName">
                                            {uploadedImages[0].name}
                                        </span>
                                        <span className="ImageUpload_fileSize">
                                            {uploadedImages[0].size}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </section>
            )}
        </div>
    );
};

export default ImageUpload;
